import { supabase } from './supabase'

/**
 * Abonnés Premium (migration 0062) — LECTURE SEULE pour le staff, plus un seul geste d'écriture :
 * l'administrateur offre un mois (motif obligatoire, journalisé en base dans `premium_evenements`).
 *
 * Tout passe par des fonctions de la base : `premium_liste_abonnes()` (réservée au staff, statuts
 * calculés côté serveur) et `premium_offrir_mois()` (réservée aux admins). Aucune table n'est
 * écrite d'ici : la console ne peut ni prolonger, ni résilier, ni marquer payé un abonnement.
 */

export type StatutAbonne = 'actif' | 'renouvellement_a_payer' | 'grace' | 'resilie' | 'termine'

export interface Abonne {
  abonnementId: string
  clientId: string
  nom: string
  email: string | null
  telephone: string | null
  statut: StatutAbonne
  depuis: string
  finPeriode: string
  renouvellementAuto: boolean
  resilieLe: string | null
  finLe: string | null
  finMotif: 'non_paiement' | 'resiliation' | 'admin' | null
  nbMois: number
  prixPeriode: number | null
  operateur: string | null
  echeanceId: string | null
  montantAPayer: number | null
  echeance: string | null
  graceJusquA: string | null
  moisOfferts: number
}

export const LIBELLE_STATUT_ABONNE: Record<StatutAbonne, string> = {
  actif: 'Actif',
  renouvellement_a_payer: 'Renouvellement à payer',
  grace: 'En grâce (impayé)',
  resilie: 'Résilié (actif jusqu’à l’échéance)',
  termine: 'Terminé',
}

export const COULEUR_STATUT_ABONNE: Record<StatutAbonne, string> = {
  actif: 'green',
  renouvellement_a_payer: 'yellow',
  grace: 'red',
  resilie: 'gray',
  termine: 'gray',
}

export const LIBELLE_FIN: Record<string, string> = {
  non_paiement: 'non-paiement (sans frais)',
  resiliation: 'résiliation',
  admin: 'décision interne',
}

const STATUTS = new Set<StatutAbonne>(['actif', 'renouvellement_a_payer', 'grace', 'resilie', 'termine'])

/** Ligne brute de `premium_liste_abonnes()` → abonné typé (statut inconnu = « terminé », jamais « actif »). */
export function abonneDepuisLigne(l: Record<string, unknown>): Abonne {
  const n = (v: unknown) => (v === null || v === undefined ? null : Number(v))
  const t = (v: unknown) => (v === null || v === undefined ? null : String(v))
  const statut = STATUTS.has(l.statut as StatutAbonne) ? (l.statut as StatutAbonne) : 'termine'
  return {
    abonnementId: String(l.subscription_id),
    clientId: String(l.client_id),
    nom: t(l.nom) ?? 'Client',
    email: t(l.email),
    telephone: t(l.telephone),
    statut,
    depuis: String(l.depuis),
    finPeriode: String(l.fin_periode),
    renouvellementAuto: l.auto_renew === true,
    resilieLe: t(l.resilie_le),
    finLe: t(l.fin_le),
    finMotif: (t(l.fin_motif) as Abonne['finMotif']) ?? null,
    nbMois: Number(l.nb_mois ?? 0),
    prixPeriode: n(l.prix_periode),
    operateur: t(l.provider),
    echeanceId: t(l.echeance_id),
    montantAPayer: n(l.montant_a_payer),
    echeance: t(l.echeance),
    graceJusquA: t(l.grace_jusqu_a),
    moisOfferts: Number(l.mois_offerts ?? 0),
  }
}

export type FiltreAbonnes = 'tous' | 'en_cours' | 'a_payer' | 'resilies' | 'termines'

export function filtrerAbonnes(liste: Abonne[], f: FiltreAbonnes): Abonne[] {
  switch (f) {
    case 'en_cours': return liste.filter((a) => a.statut !== 'termine')
    case 'a_payer': return liste.filter((a) => a.statut === 'renouvellement_a_payer' || a.statut === 'grace')
    case 'resilies': return liste.filter((a) => a.statut === 'resilie')
    case 'termines': return liste.filter((a) => a.statut === 'termine')
    default: return liste
  }
}

export interface ResumeAbonnes {
  actifs: number
  aPayer: number
  enGrace: number
  resilies: number
  termines: number
  /** Revenu mensuel récurrent estimé : prix de la période des abonnés qui renouvellent. Offerts (0 F) exclus. */
  mrr: number
}

/** Un client compte une fois : seul son abonnement le plus récent est pris en compte. */
export function resumerAbonnes(liste: Abonne[]): ResumeAbonnes {
  const enCours = liste.filter((a) => a.statut !== 'termine')
  const clientsEnCours = new Set(enCours.map((a) => a.clientId))
  const terminesSeuls = new Set(liste.filter((a) => a.statut === 'termine' && !clientsEnCours.has(a.clientId)).map((a) => a.clientId))
  return {
    actifs: enCours.length,
    aPayer: enCours.filter((a) => a.statut === 'renouvellement_a_payer').length,
    enGrace: enCours.filter((a) => a.statut === 'grace').length,
    resilies: enCours.filter((a) => a.statut === 'resilie').length,
    termines: terminesSeuls.size,
    mrr: enCours.filter((a) => a.renouvellementAuto).reduce((s, a) => s + (a.prixPeriode ?? 0), 0),
  }
}

export async function listerAbonnes(): Promise<Abonne[]> {
  const { data, error } = await supabase.rpc('premium_liste_abonnes')
  if (error) throw error
  return ((data ?? []) as Array<Record<string, unknown>>).map(abonneDepuisLigne)
}

/** Motif d'un mois offert : obligatoire (≥ 3 caractères), il est journalisé tel quel. */
export function motifValide(motif: string): boolean {
  return motif.trim().length >= 3
}

/** Offre un mois de Premium (admin). Rend la nouvelle fin de période. */
export async function offrirMois(clientId: string, motif: string): Promise<string> {
  if (!motifValide(motif)) throw new Error('motif_obligatoire')
  const { data, error } = await supabase.rpc('premium_offrir_mois', { p_client: clientId, p_motif: motif.trim() })
  if (error) throw error
  return String((data as { fin_periode?: string } | null)?.fin_periode ?? '')
}

/** Erreurs de la base → phrases. */
export function traduireErreurPremium(message: string): string {
  if (message.includes('reserve_admin')) return 'Réservé aux administrateurs.'
  if (message.includes('reserve_staff')) return 'Réservé à l’équipe SwiftEats.'
  if (message.includes('motif_obligatoire')) return 'Indiquez le motif du geste commercial (3 caractères au moins).'
  if (message.includes('client_introuvable')) return 'Client introuvable.'
  if (message.includes('premium_liste_abonnes') || message.includes('does not exist'))
    return 'La migration 0062 (abonnement Premium) n’est pas encore appliquée.'
  return message
}
